import { Brand } from '@mfb/lego';
import mixpanel from 'mixpanel-browser';

const getSessionReplayPercent = (): number => {
  const percent = Number(process.env.GATSBY_WEBSITE_MIX_PANEL_SESSION_REPLAY_PERCENT);
  if (!Number.isFinite(percent)) {
    return 0;
  }
  return Math.min(100, Math.max(0, percent));
};

export const loadMixPanel  = ()=>{
  const mixPanelApiHost  =  process.env.GATSBY_WEBSITE_MIX_PANEL_API_HOST;
  const mixPanelToken = process.env.GATSBY_WEBSITE_MIX_PANEL_TOKEN;
  if (mixPanelToken) {
    const brand = getBrandFromEnv();
    const abSite = getAbSite();
    const siteProperties = {
      Platform: getPlatform(brand),
      Brand: getActiveBrand(brand),
      'Platform Variant': 'Gatsby Website',
      ...(abSite ? {ab_site: abSite} : {}),
    };
    try {
      mixpanel.init(mixPanelToken, {
        ...(mixPanelApiHost ? {api_host: mixPanelApiHost} : {}),
        debug: false,
        autocapture: {
          pageview: 'url-with-path',
          click: true,
          scroll: true,
          submit: false,
          rage_click: true,
          dead_click: true,
          input: false,
          capture_text_content: true,
          capture_extra_attrs: ['data-category', 'data-action', 'data-label', 'data-test'],
        },
        record_sessions_percent: getSessionReplayPercent(),
        record_mask_all_inputs: true,
        record_mask_all_text: false,
        record_block_selector: '',
        persistence: 'localStorage', // the order form reads the device ID from here; both must use the same token
        hooks: {
          before_track: (eventName, properties) => [eventName, {...siteProperties, ...properties}],
        },
      });
    } catch (error) {
      console.error('Mixpanel did not start:', error);
      return;
    }
    // The GrowthBook script in gatsby-ssr.js is an inline <script>, so it
    // can only call mixpanel through window.mixpanel — assign it here.
    // If GrowthBook bucketed a user before Mixpanel finished initialising,
    // those track() calls were parked on window.__gbMixpanelQueue. Now that
    // Mixpanel is ready, run them so we don't lose those experiment events.
    if (typeof window !== 'undefined') {
      (window as any).mixpanel = mixpanel;
      const queue: Array<() => void> | undefined = (window as any).__gbMixpanelQueue;
      if (queue && queue.length) {
        queue.forEach(fn => { try { fn(); } catch { /* ignore */ } });
        (window as any).__gbMixpanelQueue = [];
      }
    }
  }
}

export const getActiveBrand = (brand: Brand): string => {
  switch(brand) {
    case Brand.MFB:
      return 'MyFoodBag';
    case Brand.BB:
      return 'BargainBox';
    case Brand.FS:
      return 'FreshStart';
    case Brand.RMM:
      return 'ReadyMadeMeals';
  }
}

export const getPlatform = (brand: Brand): string => {
  switch(brand) {
    case Brand.BB:
      return 'BB Website';
    case Brand.FS:
      return 'FS Website';
    default:
      return 'MFB Website';
  }
};

export const getBrandFromEnv = (): Brand => {
  const brandString = process.env.GATSBY_BRAND;
  switch (brandString) {
    case 'BB':
      return Brand.BB;
    case 'FS':
      return Brand.FS;
    case 'RMM':
      return Brand.RMM;
    case 'MFB':
    default:
      return Brand.MFB;
  }
};

// Variant assigned by the Cloudflare A/B snippet; undefined wherever the snippet does not run.
export const getAbSite = (): string | undefined => {
  if (typeof document === 'undefined') {
    return undefined;
  }
  return /(?:^|;\s*)ab_site=(A|B)\b/.exec(document.cookie)?.[1];
};

export const getPromoGroupCode = () => {
  if (typeof window !== 'undefined') {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('pr') || 'None';
  }
  return 'None';
}